import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PersonWeekGrid, { draftsDirty, draftsToBodies } from './PersonWeekGrid';
import ListPageToolbar from '../ui/ListPageToolbar';
import WeekPicker, { mondayOfDate, shiftWeek } from '../ui/WeekPicker';
import useWeekSlide from './useWeekSlide';
import PhoneActionBar from '../ui/PhoneActionBar';
import LoadingSpinner from '../LoadingSpinner';
import EmptyState from '../EmptyState';
import ProgressBar from '../ProgressBar';
import PhoneContextBar, { SheetField } from './PhoneContextBar';
import TimesheetActionsMenu from './TimesheetActionsMenu';
import CrewPhoneStatusPill, { crewPhonePill } from './CrewPhoneStatusPill';
import TimesheetExportDialog from './TimesheetExportDialog';
import useTimesheetExport from './useTimesheetExport';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';
import { BTN } from '../../constants/buttons';
import { FORM_INPUT_FIELD } from '../../constants/inputs';
import { formatDateTimeCompact, formatDateWeekdayDayMonth, toLocalISO } from '../../utils/dateFormat';
import { addDays, parseDate, toDateStr } from '../../utils/ganttDates';
import ConfirmDialog from '../ConfirmDialog';
import { fetchMyWeek, saveMyDay, sendMyWeek } from '../../utils/timesheetsApi';
import {
  CREW_STATE_PILL_COLOURS, STATUS_PILL_COLOURS, gagPickList, requiredBlankDays, saveErrorText, softDayComplete, weekStatusLabel,
} from '../../utils/timesheetDisplay';

/**
 * "MY TIMESHEET" — the crew screen (spec §2: a person sees only their own
 * days and statuses and NEVER any money). Since 2026-10-25 it is the SAME
 * page shell, toolbar (`ui/ListPageToolbar` — the week picker where the search
 * would be; no search, no tabs) and week grid (`PersonWeekGrid`, crew mode) as
 * the manager screen (owner: *"something similar for My time sheet. USE common
 * code"*).
 *
 * It reads `GET /api/timesheets/my-week` (the crew allowlist,
 * `server/helpers/timesheetCrewView.js`) and writes through
 * `PUT /my-days/:lineageId/:date` — one request per changed day, the body the
 * one-day form sent. Nothing here can show a price: the response carries none
 * and the grid's crew mode renders no money row.
 *
 * Authority: the crew member edits their OWN values; a value that differs from
 * the unit timecard shows amber ▲ with the unit's value in words, and a manager
 * settles it. A BUYER's value is read-only here (lock; the server refuses it).
 * 🔀 THE HAND-IN (owner, 2026-10-25): Save keeps a private draft; "Submit week"
 * sends it to the buyer (`POST /my-weeks/:lineageId/send`). 🔀 2026-10-31 (owner): ONLY A COMPLETE
 * week — the button reads "N days to complete" and takes them to the first one (the server refuses
 * `week_incomplete` too); a sent week stays THEIRS to edit (each save must keep it complete) until
 * the buyer acts on it. A buyer SAVING a change to the draft also locks it (taken over). ONE
 * status line says which of draft / sent / taken / approved the week is.
 *
 * 📤 EXPORT (owner 2026-11-01, "no money yet" — HOURS ONLY): `Actions ▾` (`TimesheetActionsMenu`
 * `crew`) — Export week (PDF / CSV) for the week on screen, and Export weeks… (the export dialog's
 * `crew` mode). On a phone the same three are buttons in the context sheet. Both go through
 * `useTimesheetExport({ crew: true })` → `GET /my-export` (the caller's own weeks, the /my-week
 * allowlist) and record no payroll run. An export reads what is SAVED; unsaved edits are not in it.
 *
 * Unsaved edits: the PAGE owns the three-way prompt (`hooks/useDirtyPrompt`).
 * This screen reports `dirty`, publishes save/discard into `saveRef` /
 * `discardRef`, and routes every click that would lose a draft (another week,
 * another person) through `continueWhenClean`.
 */
export default function CrewTimesheet({
  weekStart,
  onWeekChange,
  units,
  gags,
  continueWhenClean,
  saveRef,
  discardRef,
  onDirtyChange,
  onSaved,
  modeControl = null,
}) {
  const isMobile = useIsMobileOrderViewport();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lineageId, setLineageId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmEarly, setConfirmEarly] = useState(false); // the "week isn't finished" question
  const [jumpTo, setJumpTo] = useState(null); // { date, n } — Submit on an unfinished week goes there
  const seq = useRef(0);
  const { run: runExport, busy: exportBusy } = useTimesheetExport({ crew: true });
  const [exportOpen, setExportOpen] = useState(false);
  const [exportError, setExportError] = useState(null);
  const exportWeek = async (format) => {
    setExportError(null);
    const r = await runExport({ from: weekStart, to: weekStart, format });
    if (!r.ok) setExportError(r.error);
  };
  const openExport = () => { setExportError(null); setExportOpen(true); };
  const actionsMenu = (variant = 'menu', close = null) => (
    <TimesheetActionsMenu crew variant={variant} weekStart={weekStart} busy={exportBusy} closeSheet={close}
      onOpenExport={openExport} onExportWeek={exportWeek} />
  );

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const r = await fetchMyWeek(weekStart);
      if (mine !== seq.current) return;
      if (!r.ok) { setError((r.body && r.body.error) || 'Could not load your timesheet.'); setData(null); return; }
      setData(r.body);
    } catch (e) {
      if (mine === seq.current) setError('Could not reach the server. Check your connection and try again.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  // Crew fill in up to NEXT week (owner 2026-11-01, ruling A; the server refuses later weeks
  // `week_not_open`). A week carried in from the manager screen beyond it is brought back.
  const latestWeek = shiftWeek(mondayOfDate(toLocalISO(new Date())), 1);
  useEffect(() => { if (weekStart > latestWeek) onWeekChange(latestWeek); }, [weekStart, latestWeek, onWeekChange]);

  const people = useMemo(() => (data && Array.isArray(data.people) ? data.people : []), [data]);
  // Desktop: the week card slides in from the side the picker moved to (owner 2026-10-30).
  const weekSlideRef = useWeekSlide(data ? data.week_start : null, weekStart);
  const person = people.find((p) => p.lineage_id === lineageId) || people[0] || null;
  const submitted = person ? person.status === 'submitted' : false;
  const crewState = person ? (person.crew_state || 'draft') : 'draft';
  // 🔀 2026-10-31 (owner: "it should still be editable until the buyer edits it"): a SENT week stays
  // theirs until the buyer acts on it (`taken_at`). Locked: approved, taken over, or sent-then-acted-on.
  const sentOpen = crewState === 'sent' && !(person && person.taken_at);
  const locked = submitted || (crewState !== 'draft' && !sentOpen);
  const dirty = draftsDirty(drafts);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const { gagByTitle, gagTitles } = useMemo(() => gagPickList(gags), [gags]);

  const discard = useCallback(() => { setDrafts({}); setSaveError(null); }, []);

  const save = useCallback(async () => {
    if (!person) return false;
    const { bodies, error: bodyError } = draftsToBodies(drafts, person, gagByTitle);
    if (bodyError) { setSaveError(bodyError); return false; }
    setSaving(true);
    setSaveError(null);
    try {
      for (const { date, body } of bodies) {
        // eslint-disable-next-line no-await-in-loop
        const r = await saveMyDay(person.lineage_id, date, body);
        if (!r.ok) {
          // A lock that landed while they typed (sent elsewhere, taken over): say so and show the locked week.
          if (r.body && r.body.code === 'week_locked_for_crew') load();
          setSaveError(saveErrorText(r.body, 'Could not save your week.'));
          return false;
        }
        const next = r.body && r.body.person;
        if (next) {
          setData((prev) => ({
            ...(prev || {}),
            people: (prev && prev.people ? prev.people : []).map((p) => (p.lineage_id === next.lineage_id ? next : p)),
          }));
        }
        setDrafts((prev) => { const n = { ...prev }; delete n[date]; return n; });
      }
      onSaved?.();
      return true;
    } catch (e) {
      setSaveError('Could not reach the server. Your changes are still here — try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [person, drafts, gagByTitle, onSaved, load]);

  useEffect(() => {
    if (saveRef) saveRef.current = save;
    if (discardRef) discardRef.current = discard;
  }, [save, discard, saveRef, discardRef]);

  const hasConflicts = !!person && !locked && person.days.some((d) => d.conflicts && d.conflicts.length > 0);
  const blanks = person ? requiredBlankDays(person.days) : [];
  // SOFT (owner 2026-10-30): the bar follows unsaved edits; the server decides again on save.
  const daysDone = person ? person.days.filter((d) => softDayComplete(d, drafts[d.date])).length : 0;

  // 🔀 2026-10-31 (owner: "im able to submit weeks that are not filled out?! fundimental error!!"):
  // Submit needs a COMPLETE week — the server refuses one that is not (`week_incomplete`), and the
  // button says so and takes them to the first unfinished day instead of asking "submit anyway?".
  const emptyWeek = !!person && !person.days.some((d) => d.complete && !d.default_rest);
  const goToDay = (date) => setJumpTo((j) => ({ date, n: (j ? j.n : 0) + 1 }));
  const doSend = async () => {
    if (!person) return;
    setSending(true);
    setSaveError(null);
    try {
      const r = await sendMyWeek(person.lineage_id, weekStart);
      if (!r.ok) {
        setSaveError((r.body && r.body.error) || 'Could not submit your week.');
        if (r.body && r.body.code === 'week_locked_for_crew') load();
        if (r.body && r.body.code === 'week_incomplete' && Array.isArray(r.body.days) && r.body.days.length) goToDay(r.body.days[0]);
        return;
      }
      const next = r.body && r.body.person;
      if (next) setData((prev) => ({ ...(prev || {}), people: (prev && prev.people ? prev.people : []).map((p) => (p.lineage_id === next.lineage_id ? next : p)) }));
    } catch (e) {
      setSaveError('Could not reach the server. Try again.');
    } finally {
      setSending(false);
    }
  };
  // EARLY SUBMIT ASKS FIRST (owner 2026-11-01: "submitting any time is allowed with a warning,
  // week not finished. are you sure you want to submit now"): a week whose Sunday is not over yet.
  const weekSunday = toDateStr(addDays(parseDate(weekStart), 6));
  const weekNotFinished = weekStart >= mondayOfDate(toLocalISO(new Date()));
  const submitWeek = () => {
    if (blanks.length > 0) return goToDay(blanks[0].date);
    if (weekNotFinished) return setConfirmEarly(true);
    return doSend();
  };
  const toComplete = blanks.length;
  const sendLabel = sending ? 'Submitting…'
    : toComplete > 0 ? `${toComplete} ${toComplete === 1 ? 'day' : 'days'} to complete` : 'Submit week';

  /** ONE status line: pill + note (owner-approved mock, 2026-10-25). */
  const statusLine = (() => {
    if (!person) return null;
    if (submitted) {
      return { pill: 'Approved', cls: STATUS_PILL_COLOURS.submitted, lock: true, note: `Approved ${formatDateTimeCompact(person.submitted_at)} · costs fixed` };
    }
    if (crewState === 'sent' && !sentOpen) {
      return { pill: 'Sent to buyer', cls: CREW_STATE_PILL_COLOURS.sent, lock: true, note: `${person.taken_by_name || 'Your buyer'} edited your week ${formatDateTimeCompact(person.taken_at)} · locked for you` };
    }
    if (sentOpen) {
      return { pill: 'Sent to buyer', cls: CREW_STATE_PILL_COLOURS.sent, lock: false, note: `Submitted ${formatDateTimeCompact(person.sent_at)} · you can still change it until your buyer edits it` };
    }
    if (crewState === 'taken') {
      return { pill: 'Taken over by buyer', cls: CREW_STATE_PILL_COLOURS.taken, lock: true, note: `${person.taken_by_name || 'Your buyer'} edited your week ${formatDateTimeCompact(person.taken_at)} · locked for you` };
    }
    const n = blanks.length;
    const parts = [
      person.saved_at ? `Saved ${formatDateTimeCompact(person.saved_at)}` : null,
      // "only you and your buyer can see it" DROPPED (owner 2026-10-28: "we don't need it — saves a row").
      n > 0 ? `${n} ${n === 1 ? 'day still needs' : 'days still need'} details` : null,
    ].filter(Boolean);
    // "Draft · To complete" → "Draft" (owner 2026-10-30: the 2/7 bar already says it); other statuses stay.
    return { pill: person.status === 'not_entered' ? 'Draft' : `Draft · ${weekStatusLabel(person.status)}`, cls: CREW_STATE_PILL_COLOURS.draft, lock: false, note: parts.join(' · ') };
  })();

  const changeWeek = (w) => { if (w <= latestWeek) continueWhenClean(() => onWeekChange(w)); };
  const personSelect = (className) => (
    <select className={className} value={person ? person.lineage_id : ''} data-testid="crew-person-select"
      onChange={(e) => { const v = e.target.value; continueWhenClean(() => { setLineageId(v); setDrafts({}); }); }}>
      {people.map((p) => <option key={p.lineage_id} value={p.lineage_id}>{p.name}</option>)}
    </select>
  );

  return (
    <div data-testid="crew-timesheet">
      {isMobile ? (
        // 📱 Phone header (mock B, owner 2026-10-27): one chip + week; the switches live in its sheet.
        <PhoneContextBar idPrefix="crew" weekStart={weekStart} onWeekChange={changeWeek} maxWeekStart={latestWeek}
          summary={people.length > 1 && person ? `My timesheet · ${person.name}` : 'My timesheet'}>
          {({ close }) => (
            <>
              {modeControl && <SheetField label="Timesheet">{modeControl}</SheetField>}
              {people.length > 1 && <SheetField label="Timesheet for">{personSelect(FORM_INPUT_FIELD)}</SheetField>}
              {/* The sheet sits ABOVE a modal, so each action closes it first. */}
              <SheetField label="Export">{actionsMenu('sheet', close)}</SheetField>
            </>
          )}
        </PhoneContextBar>
      ) : (
        <ListPageToolbar
          marginClassName="mb-2"
          data-testid="crew-toolbar"
          picker={<WeekPicker weekStart={weekStart} onChange={changeWeek} idPrefix="crew-week" maxWeekStart={latestWeek} />}
          tabs={people.length > 1 ? (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span className="whitespace-nowrap">Timesheet for</span>
              {personSelect(`${FORM_INPUT_FIELD} w-auto`)}
            </label>
          ) : null}
          actions={actionsMenu()}
        />
      )}
      {exportError && !exportOpen && (
        <p role="alert" className="mb-2 text-sm text-red-700 dark:text-red-300" data-testid="crew-export-error">{exportError}</p>
      )}
      <TimesheetExportDialog
        crew
        open={exportOpen}
        weekStart={weekStart}
        busy={exportBusy}
        error={exportError}
        onCancel={() => setExportOpen(false)}
        onExport={async (request) => {
          setExportError(null);
          const r = await runExport(request);
          if (r.ok) setExportOpen(false); else setExportError(r.error);
        }}
      />

      {loading && !data ? (
        <div className="py-10"><LoadingSpinner /></div>
      ) : error ? (
        <p role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </p>
      ) : !person ? (
        <EmptyState
          title="No timesheet for you here yet"
          description="You are not on this department's crew list for the current budget. Ask your manager to add you."
          testId="crew-timesheet-empty"
        />
      ) : (
        <section ref={weekSlideRef} aria-label="My week" data-testid="crew-week-card"
          className="space-y-2 rounded-lg border border-gray-200 bg-white p-2 md:p-3 dark:border-gray-700 dark:bg-gray-800 max-md:flex max-md:flex-col max-md:gap-2 max-md:space-y-0">
          <div className="flex flex-wrap items-center gap-2 text-sm max-md:contents" data-testid="crew-status-line">
            {/* Pill + note + legend: ONE wrapping row on a phone too (owner 2026-10-28: "saves a row"). */}
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              {isMobile ? (
                // 📱 ONE pill beside the bar, the note behind its padlock (owner 2026-11-01) — CrewPhoneStatusPill.
                <CrewPhoneStatusPill pill={crewPhonePill({ person, submitted, crewState, sentOpen, note: statusLine.note })} />
              ) : (
              <span data-testid="crew-week-status" className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusLine.cls}`}>
                {statusLine.pill}
              </span>
              )}
              {(isMobile || !statusLine.lock) && (
                // Days complete out of the week (owner 2026-10-28: "a small completion bar like we use for
                // deliveries") — THE shared ProgressBar + count, the order table's delivery pattern.
                // A default weekend Rest is complete, so a finished week reads 7/7.
                <span data-testid="crew-week-progress" className="flex items-center gap-1.5"
                  title={`${daysDone} of ${person.days.length} days complete`}>
                  <ProgressBar done={daysDone} total={person.days.length} className="w-16" ariaLabel="Days complete" />
                  <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">{daysDone}/{person.days.length}</span>
                </span>
              )}
              {!isMobile && (statusLine.lock || statusLine.note) && (
                <span data-testid="crew-week-note" className="text-gray-700 dark:text-gray-300">
                  {statusLine.lock && <span aria-hidden="true">🔒 </span>}{statusLine.note}
                </span>
              )}
              {hasConflicts && (
                <span className="text-gray-700 dark:text-gray-300" data-testid="crew-conflict-legend">
                  <span aria-hidden="true" className="text-amber-700 dark:text-amber-400">▲</span> differs from the unit timecard — your buyer will review
                </span>
              )}
            </div>
            {/* Discard · Save · Submit week UP on the status line, top-right (owner
                2026-10-25: the week and its buttons on one laptop screen). */}
            {!locked && (crewState === 'draft' || dirty) && (
              // 📱 Below `md` the row is the pinned bottom bar: Submit week always (thumb zone,
              // mock B 2026-10-27), Discard / Save join it only while changed. Its wrapper goes
              // LAST on a phone so the bar's spacer pads the card's end, not its top.
              <div className="md:contents max-md:order-last">
              <PhoneActionBar pinned fill className="ml-auto flex justify-end gap-2" testId="crew-week-actions">
                <button type="button" className={`${BTN.secondary} ${dirty ? '' : 'max-md:hidden'}`} disabled={!dirty || saving} onClick={discard}>Discard</button>
                <button type="button" data-testid="crew-week-save" className={`${BTN.confirm} ${dirty ? '' : 'max-md:hidden'}`} disabled={!dirty || saving} onClick={save}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                {crewState === 'draft' && (
                  <button type="button" data-testid="crew-week-send" className={toComplete > 0 ? BTN.secondary : BTN.primary}
                    disabled={dirty || saving || sending || (toComplete === 0 && emptyWeek)}
                    title={dirty ? 'Save your changes first'
                      : toComplete > 0 ? 'Finish every working day first — this takes you to the next one'
                        : emptyWeek ? 'Nothing entered this week yet'
                          : 'Hand your week to your buyer — you can still change it until they edit it'}
                    onClick={submitWeek}>
                    {sendLabel}
                  </button>
                )}
              </PhoneActionBar>
              </div>
            )}
          </div>
          <PersonWeekGrid
            person={person}
            mode="crew"
            onStepWeek={(d) => changeWeek(shiftWeek(weekStart, d))}
            nextWeekOpen={weekStart < latestWeek}
            requestedWeek={weekStart}
            jumpTo={jumpTo}
            units={units}
            gagTitles={gagTitles}
            today={toLocalISO(new Date())}
            readOnly={locked || saving}
            drafts={drafts}
            onDraftsChange={setDrafts}
            idPrefix="crew-week-grid"
          />
          {saveError && (
            <p role="alert" className="text-sm text-red-700 dark:text-red-300" data-testid="crew-save-error">{saveError}</p>
          )}
        </section>
      )}
      <ConfirmDialog
        open={confirmEarly}
        title="This week isn't finished yet"
        message={`It runs until ${formatDateWeekdayDayMonth(weekSunday)}. Submit it now anyway? You can still change it until your buyer edits it.`}
        confirmLabel="Submit now"
        cancelLabel="Not yet"
        initialFocus="cancel"
        onConfirm={() => { setConfirmEarly(false); doSend(); }}
        onCancel={() => setConfirmEarly(false)}
      />
    </div>
  );
}
